import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { log } from '@/utils/vueLogger.js';

const DB_NAME = 'VueSyncQueue';
const STORE_NAME = 'pendingOperations';
const DB_VERSION = 1;

class OfflineSyncQueueService {
	constructor() {
		this.db = null;
		this.handlers = new Map();
		this.isProcessing = false;
		this.onEnqueuedCallbacks = [];
		this.onProcessedCallbacks = [];
		this.onFailedCallbacks = [];

		this.initDB();
		this.initOnlineListener();
	}

	async initDB() {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(DB_NAME, DB_VERSION);

			request.onerror = () => reject(request.error);
			request.onsuccess = () => {
				this.db = request.result;
				resolve();
			};

			request.onupgradeneeded = (event) => {
				const db = event.target.result;
				if (!db.objectStoreNames.contains(STORE_NAME)) {
					const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
					store.createIndex('timestamp', 'timestamp', { unique: false });
					store.createIndex('userId', 'userId', { unique: false });
				}
			};
		});
	}

	initOnlineListener() {
		window.addEventListener('message', (event) => {
			if (event.origin !== window.location.origin) return;
			if (event.data?.type === 'SYNC_COMPLETED' && event.data?.data?.app === 'ALL') {
				log('info', 'OfflineSyncQueue', '🌐 Sync completed, processing pending queue...');
				setTimeout(() => this.processQueue(), 3000);
			}
		});
	}

	getCurrentUserId() {
		return iframeDataBridge.getCurrentUser()?.id ?? null;
	}

	register(type, handler) {
		this.handlers.set(type, handler);
	}

	async enqueue(type, payload, label) {
		if (!this.db) await this.initDB();

		const entry = {
			id: crypto.randomUUID(),
			type,
			payload: JSON.parse(JSON.stringify(payload)),
			label,
			userId: this.getCurrentUserId(),
			timestamp: Date.now(),
			retries: 0,
		};

		return new Promise((resolve, reject) => {
			const tx = this.db.transaction([STORE_NAME], 'readwrite');
			const store = tx.objectStore(STORE_NAME);
			const request = store.add(entry);

			request.onsuccess = () => {
				log('info', 'OfflineSyncQueue', `📥 Queued for next online session: ${label}`);
				this.notifyEnqueued(entry);
				resolve(entry.id);
			};
			request.onerror = () => reject(request.error);
		});
	}

	async processQueue() {
		if (this.isProcessing || !iframeDataBridge.isOnline()) return;

		this.isProcessing = true;

		try {
			const entries = await this.getEntriesForCurrentUser();

			if (entries.length === 0) return;

			log('info', 'OfflineSyncQueue', `🔄 Processing ${entries.length} pending operation(s)...`);

			for (const entry of entries) {
				const handler = this.handlers.get(entry.type);

				if (!handler) {
					log('warn', 'OfflineSyncQueue', `⚠️ No handler registered for type: ${entry.type}`);
					continue;
				}

				try {
					await handler(entry.payload);
					await this.deleteEntry(entry.id);
					log('success', 'OfflineSyncQueue', `✅ Synced: ${entry.label}`);
					this.notifyProcessed(entry);
				} catch (error) {
					await this.incrementRetries(entry.id);
					log('warn', 'OfflineSyncQueue', `⚠️ Failed to sync: ${entry.label} — ${error.message}`);
					this.notifyFailed(entry, error);
				}
			}
		} finally {
			this.isProcessing = false;
		}
	}

	async getEntriesForCurrentUser() {
		if (!this.db) await this.initDB();

		const userId = this.getCurrentUserId();

		return new Promise((resolve, reject) => {
			const tx = this.db.transaction([STORE_NAME], 'readonly');
			const store = tx.objectStore(STORE_NAME);
			const index = store.index('userId');
			const request = index.getAll(userId);

			request.onsuccess = () => resolve(request.result.sort((a, b) => a.timestamp - b.timestamp));
			request.onerror = () => reject(request.error);
		});
	}

	async deleteEntry(id) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const tx = this.db.transaction([STORE_NAME], 'readwrite');
			const store = tx.objectStore(STORE_NAME);
			const request = store.delete(id);

			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async incrementRetries(id) {
		if (!this.db) await this.initDB();

		return new Promise((resolve) => {
			const tx = this.db.transaction([STORE_NAME], 'readwrite');
			const store = tx.objectStore(STORE_NAME);
			const getRequest = store.get(id);

			getRequest.onsuccess = () => {
				const entry = getRequest.result;
				if (entry) {
					entry.retries = (entry.retries || 0) + 1;
					store.put(entry);
				}
				resolve();
			};
			getRequest.onerror = () => resolve();
		});
	}

	async getPendingCount() {
		const entries = await this.getEntriesForCurrentUser();
		return entries.length;
	}

	onEnqueued(callback) {
		this.onEnqueuedCallbacks.push(callback);
	}

	onProcessed(callback) {
		this.onProcessedCallbacks.push(callback);
	}

	onFailed(callback) {
		this.onFailedCallbacks.push(callback);
	}

	notifyEnqueued(entry) {
		this.onEnqueuedCallbacks.forEach((cb) => {
			try {
				cb(entry);
			} catch (error) {
				log('error', 'OfflineSyncQueue', `❌ onEnqueued callback error: ${error.message}`);
			}
		});
	}

	notifyProcessed(entry) {
		this.onProcessedCallbacks.forEach((cb) => {
			try {
				cb(entry);
			} catch (error) {
				log('error', 'OfflineSyncQueue', `❌ onProcessed callback error: ${error.message}`);
			}
		});
	}

	notifyFailed(entry, error) {
		this.onFailedCallbacks.forEach((cb) => {
			try {
				cb(entry, error);
			} catch (err) {
				log('error', 'OfflineSyncQueue', `❌ onFailed callback error: ${err.message}`);
			}
		});
	}
}

const offlineSyncQueue = new OfflineSyncQueueService();
export default offlineSyncQueue;

export const registerQueueHandler = (type, handler) => offlineSyncQueue.register(type, handler);
export const enqueueOperation = (type, payload, label) => offlineSyncQueue.enqueue(type, payload, label);
export const processOfflineQueue = () => offlineSyncQueue.processQueue();
export const getPendingCount = () => offlineSyncQueue.getPendingCount();
export const onEnqueued = (callback) => offlineSyncQueue.onEnqueued(callback);
export const onProcessed = (callback) => offlineSyncQueue.onProcessed(callback);
export const onFailed = (callback) => offlineSyncQueue.onFailed(callback);
